import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { differenceInDays, subDays } from 'date-fns'
import { useMemo } from 'react'
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts'
import { DateRangePicker } from '~/components/date-picker'
import { Avatar } from '~/features/avatar'
import { createPlaceholderData } from '~/features/visualizations/placeholder-data'
import { formatDateBySeries } from '~/helpers/dateHelper'
import { useTimespanPreference, useWeekStartsOn } from '~/hooks'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { useLoader } from '~/modules/loader'
import { useTRPC } from '~/services/trpc'

import { type AnalyticsQueryFilter } from '@hap/analytics'
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Icon,
  Progress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Y_AXIS_DEFAULT_PROPS,
  cn,
} from '@hap/components'
import { Timespan, toPercent } from '@hap/utils'

const MIN_EVALUATIONS = 10

export const Route = createFileRoute(
  '/_authenticated/$alias/_app/scorecard/$scorecardId/',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const params = Route.useParams()
  const trpc = useTRPC()

  const weekStartsOn = useWeekStartsOn()
  const { timespan, setTimespanPreference } = useTimespanPreference({
    startOffset: (date) => subDays(date, 30),
  })

  const staticTimespan = useMemo(
    () => Timespan.toStaticTimespan(timespan, weekStartsOn),
    [timespan, weekStartsOn],
  )

  const previousTimespan = useMemo(() => {
    const duration = differenceInDays(staticTimespan.end, staticTimespan.start)
    return {
      start: subDays(staticTimespan.start, duration),
      end: staticTimespan.start,
    }
  }, [staticTimespan])

  const scorecardId = params.scorecardId

  const { data: scorecard } = useQuery(trpc.scorecards.getById.queryOptions(scorecardId))

  const baseFilters: AnalyticsQueryFilter[] = useMemo(
    () => [
      {
        field: 'scorecardId',
        op: 'eq',
        value: scorecardId,
        dataset: 'evaluations',
      },
      {
        field: 'publishedAt',
        op: 'isNotNull',
        dataset: 'evaluations',
      },
    ],
    [scorecardId],
  )

  // ── KPIs ────────────────────────────────────────────────────────────────
  const { data: kpiData, loading: kpiLoading } = useAnalytics({
    dataset: 'interactions',
    timespan: staticTimespan,
    filters: baseFilters,
    aggregations: {
      avgScore: {
        field: 'averageEvaluationScore',
        op: 'avg',
      },
      passCount: {
        op: 'countIf',
        conditions: [{ field: 'metQualityGoal', op: 'eq', value: true }],
      },
      totalEvaluations: {
        op: 'countIf',
        conditions: [{ field: 'hasEvaluation', op: 'eq', value: true }],
      },
      totalInteractions: {
        op: 'countDistinct',
        field: 'id',
      },
    },
  })

  const { data: prevKpiData } = useAnalytics(
    {
      dataset: 'interactions',
      timespan: previousTimespan,
      filters: baseFilters,
      aggregations: {
        avgScore: {
          field: 'averageEvaluationScore',
          op: 'avg',
        },
        passCount: {
          op: 'countIf',
          conditions: [{ field: 'metQualityGoal', op: 'eq', value: true }],
        },
        totalEvaluations: {
          op: 'countIf',
          conditions: [{ field: 'hasEvaluation', op: 'eq', value: true }],
        },
        totalInteractions: {
          op: 'countDistinct',
          field: 'id',
        },
      },
    },
    { enabled: !!kpiData },
  )

  // ── Score Trend ─────────────────────────────────────────────────────────
  const { data: trendData, loading: trendLoading } = useAnalytics({
    dataset: 'interactions',
    timespan: staticTimespan,
    filters: baseFilters,
    series: 'day',
    aggregations: {
      avgScore: {
        field: 'averageEvaluationScore',
        op: 'avg',
      },
      passCount: {
        op: 'countIf',
        conditions: [{ field: 'metQualityGoal', op: 'eq', value: true }],
      },
      totalEvaluations: {
        op: 'countIf',
        conditions: [{ field: 'hasEvaluation', op: 'eq', value: true }],
      },
    },
  })

  // ── Criteria Breakdown ──────────────────────────────────────────────────
  const { data: criteriaData, loading: criteriaLoading } = useAnalytics({
    dataset: 'evaluationCriteriaScores',
    timespan: staticTimespan,
    filters: baseFilters,
    aggregations: {
      scorecardCriteriaId: {
        field: 'scorecardCriteriaId',
        hydrate: true,
        dataset: 'evaluationCriteriaScores',
      },
      avgScore: {
        field: 'score',
        op: 'avg',
        dataset: 'evaluationCriteriaScores',
      },
      count: {
        op: 'count',
        field: 'id',
        dataset: 'evaluationCriteriaScores',
      },
    },
  })

  // ── Agent Snapshot ──────────────────────────────────────────────────────
  const { data: agentData, loading: agentLoading } = useAnalytics({
    dataset: 'interactions',
    timespan: staticTimespan,
    filters: baseFilters,
    aggregations: {
      handledBy: {
        field: 'handledBy',
        hydrate: true,
        dataset: 'interactions',
      },
      avgScore: {
        field: 'averageEvaluationScore',
        op: 'avg',
      },
      totalEvaluations: {
        op: 'countIf',
        conditions: [{ field: 'hasEvaluation', op: 'eq', value: true }],
      },
      totalInteractions: {
        op: 'countDistinct',
        field: 'id',
      },
    },
  })

  // ── Intent Breakdown ────────────────────────────────────────────────────
  const { data: intentData, loading: intentLoading } = useAnalytics({
    dataset: 'interactions',
    timespan: staticTimespan,
    filters: baseFilters,
    aggregations: {
      intentId: {
        field: 'intentId',
        hydrate: true,
        dataset: 'interactions',
      },
      avgScore: {
        field: 'averageEvaluationScore',
        op: 'avg',
      },
      count: {
        op: 'count',
        field: 'id',
      },
    },
  })

  // ── Sentiment Breakdown ─────────────────────────────────────────────────
  const { data: sentimentData, loading: sentimentLoading } = useAnalytics({
    dataset: 'interactions',
    timespan: staticTimespan,
    filters: baseFilters,
    aggregations: {
      sentiment: {
        field: 'overallCustomerSentimentLabelId',
        hydrate: true,
        dataset: 'interactions',
      },
      avgScore: {
        field: 'averageEvaluationScore',
        op: 'avg',
      },
      count: {
        op: 'count',
        field: 'id',
      },
    },
  })

  useLoader(
    kpiLoading ||
      trendLoading ||
      criteriaLoading ||
      agentLoading ||
      intentLoading ||
      sentimentLoading,
  )

  // ── Derived KPIs ────────────────────────────────────────────────────────
  const current = kpiData?.[0]
  const previous = prevKpiData?.[0]

  const avgScore = current ? parseFloat(current.avgScore) : null
  const prevAvgScore = previous ? parseFloat(previous.avgScore) : null

  const passCount = current ? parseFloat(current.passCount) : 0
  const totalEvaluations = current ? parseFloat(current.totalEvaluations) : 0
  const passRate = totalEvaluations > 0 ? passCount / totalEvaluations : null

  const prevPassCount = previous ? parseFloat(previous.passCount) : 0
  const prevTotalEvaluations = previous ? parseFloat(previous.totalEvaluations) : 0
  const prevPassRate =
    prevTotalEvaluations > 0 ? prevPassCount / prevTotalEvaluations : null

  const totalInteractions = current ? parseFloat(current.totalInteractions) : 0
  const coverage = totalInteractions > 0 ? totalEvaluations / totalInteractions : null

  const prevTotalInteractions = previous ? parseFloat(previous.totalInteractions) : 0
  const prevCoverage =
    prevTotalInteractions > 0 ? prevTotalEvaluations / prevTotalInteractions : null

  const evaluationsTrend =
    current && previous && parseFloat(previous.totalEvaluations) !== 0
      ? (parseFloat(current.totalEvaluations) - parseFloat(previous.totalEvaluations)) /
        parseFloat(previous.totalEvaluations)
      : null

  // ── Trend Chart Data ────────────────────────────────────────────────────
  const formattedTrendData = useMemo(() => {
    if (!trendData) return createPlaceholderData(staticTimespan, 'day')
    return trendData.map((d) => {
      const time = formatDateBySeries(d.time, 'day')
      const score = parseFloat(d.avgScore as string) || 0
      const pc = parseFloat(d.passCount as string) || 0
      const te = parseFloat(d.totalEvaluations as string) || 0
      return {
        time,
        avgScore: score,
        passRate: te > 0 ? pc / te : 0,
      }
    })
  }, [trendData, staticTimespan])

  // ── Criteria Impact ─────────────────────────────────────────────────────
  const criteriaWithImpact = useMemo(() => {
    if (!criteriaData || !scorecard) return []

    const weightMap = new Map<string, number>()
    for (const section of scorecard.sections) {
      for (const c of section.criteria) {
        weightMap.set(c.criterion.id, c.weight ?? c.criterion.defaultWeight ?? 1)
      }
    }

    return criteriaData
      .map((row) => {
        const hydrated = (row as any).scorecardCriteria
        const criteriaId = hydrated?.scorecardCriteriaId
        const score = parseFloat(row.avgScore as string) || 0
        const count = parseFloat(row.count as string) || 0
        const weight = weightMap.get(criteriaId) ?? 1
        const impact = (1 - score) * count * weight

        const criteria = scorecard.sections.flatMap((s) => s.criteria)

        const criterion = criteria.find((c) => c.criterion.id === row.scorecardCriteriaId)
        console.log({ criteriaId, row, criteria, criterion })

        return {
          id: row.scorecardCriteriaId,
          name: criterion?.criterion.name,
          avgScore: score,
          count,
          weight,
          impact,
          criteria: criterion,
        }
      })
      .filter((c) => c.count >= MIN_EVALUATIONS && c.avgScore < 0.85)
      .sort((a, b) => b.impact - a.impact)
  }, [criteriaData, scorecard])

  // ── Agent Coaching ──────────────────────────────────────────────────────
  const { topAgents, bottomAgents } = useMemo(() => {
    if (!agentData) return { topAgents: [], bottomAgents: [] }

    const valid = agentData
      .map((row) => {
        const agent = (row as any).handledBy
        const score = parseFloat(row.avgScore as string) || 0
        const evals = parseFloat(row.totalEvaluations as string) || 0
        const interactions = parseFloat(row.totalInteractions as string) || 0
        return {
          id: agent?.id as string | undefined,
          firstName: agent?.firstName as string | undefined,
          lastName: agent?.lastName as string | undefined,
          avatar: agent?.avatar as string | undefined,
          avgScore: score,
          totalEvals: evals,
          coverage: interactions > 0 ? evals / interactions : 0,
        }
      })
      .filter((a) => a.totalEvals >= MIN_EVALUATIONS)
      .sort((a, b) => b.avgScore - a.avgScore)

    return {
      topAgents: valid.slice(0, 5),
      bottomAgents: [...valid].reverse().slice(0, 5),
    }
  }, [agentData])

  // ── Intent Breakdown ────────────────────────────────────────────────────
  const intents = useMemo(() => {
    if (!intentData) return []
    return intentData
      .map((row) => {
        const intent = (row as any).intent
        const score = parseFloat(row.avgScore as string) || 0
        const count = parseFloat(row.count as string) || 0
        return {
          id: intent?.id as string | undefined,
          name: (intent?.label as string) ?? 'Unknown',
          avgScore: score,
          count,
        }
      })
      .filter((i) => i.count >= MIN_EVALUATIONS)
      .sort((a, b) => a.avgScore - b.avgScore)
  }, [intentData])

  // ── Sentiment Breakdown ─────────────────────────────────────────────────
  const sentiments = useMemo(() => {
    if (!sentimentData) return []
    return sentimentData
      .map((row) => {
        const sentiment = (row as any).overallCustomerSentimentLabel
        const score = parseFloat(row.avgScore as string) || 0
        const count = parseFloat(row.count as string) || 0
        console.log({ sentiment: row })
        return {
          id: sentiment?.id as string | undefined,
          name: (sentiment?.label as string) ?? 'Unknown',
          color: sentiment?.color as string | undefined,
          avgScore: score,
          count,
        }
      })
      .filter((s) => s.count >= MIN_EVALUATIONS)
      .sort((a, b) => a.avgScore - b.avgScore)
  }, [sentimentData])

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DateRangePicker value={timespan} onValueChange={setTimespanPreference} />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Average Quality Score"
          value={avgScore}
          previousValue={prevAvgScore}
          format={(v) => toPercent(v)}
          isLoading={kpiLoading}
        />
        <KpiCard
          title="Pass Rate"
          value={passRate}
          previousValue={prevPassRate}
          format={(v) => toPercent(v)}
          isLoading={kpiLoading}
        />
        <KpiCard
          title="Evaluations"
          value={totalEvaluations}
          previousValue={prevTotalEvaluations}
          format={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v))}
          isLoading={kpiLoading}
          trend={evaluationsTrend}
        />
        <KpiCard
          title="Coverage"
          value={coverage}
          previousValue={prevCoverage}
          format={(v) => toPercent(v)}
          isLoading={kpiLoading}
        />
      </div>

      {/* Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Score Trend</CardTitle>
        </CardHeader>
        <CardContent>
          {trendLoading ? (
            <Skeleton loading className="h-[200px] w-full" />
          ) : (
            <ChartContainer
              config={{
                avgScore: {
                  label: 'Avg Score',
                  color: '#D8BFD8',
                  unit: '%',
                  formatter: (value) => toPercent(value),
                },
                passRate: {
                  label: 'Pass Rate',
                  color: 'var(--sky-blue)',
                  unit: '%',
                  formatter: (value) => toPercent(value),
                },
              }}
              className="h-[200px] w-full"
            >
              <ComposedChart data={formattedTrendData}>
                <Area
                  dataKey="passRate"
                  fillOpacity={0.05}
                  strokeWidth={0.5}
                  isAnimationActive={false}
                  fill="var(--sky-blue)"
                  stroke="var(--sky-blue)"
                />
                <CartesianGrid />
                <Line
                  dataKey="avgScore"
                  stroke="#D8BFD8"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
                <XAxis dataKey="time" strokeOpacity={0.3} />
                <YAxis
                  orientation="right"
                  domain={[0, 1]}
                  {...Y_AXIS_DEFAULT_PROPS}
                  tickFormatter={toPercent}
                  strokeOpacity={0.3}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
              </ComposedChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Criteria + Agents */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Criteria at Risk */}
        <Card>
          <CardHeader>
            <CardTitle>Criteria at Risk</CardTitle>
          </CardHeader>
          <CardContent>
            {criteriaLoading || !scorecard ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} loading className="h-12 w-full" />
                ))}
              </div>
            ) : criteriaWithImpact.length === 0 ? (
              <EmptyState message="No criteria with sufficient evaluation volume" />
            ) : (
              <div className="space-y-4">
                {criteriaWithImpact.slice(0, 10).map((c) => (
                  <div key={c.id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{c.name}</span>
                      <span
                        className={cn('font-mono', {
                          'text-red-500': c.avgScore < 0.7,
                          'text-yellow-500': c.avgScore >= 0.7 && c.avgScore < 0.9,
                          'text-green-500': c.avgScore >= 0.9,
                        })}
                      >
                        {toPercent(c.avgScore)}
                      </span>
                    </div>
                    <Progress
                      value={c.avgScore}
                      className={cn('h-2', {
                        '[&>*]:bg-red-500': c.avgScore < 0.7,
                        '[&>*]:bg-yellow-500': c.avgScore >= 0.7 && c.avgScore < 0.9,
                        '[&>*]:bg-green-500': c.avgScore >= 0.9,
                      })}
                    />
                    <div className="text-muted-foreground text-xs">
                      {Math.round(c.count)} evaluations · Weight: {c.weight}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Agent Coaching Snapshot */}
        <Card>
          <CardHeader>
            <CardTitle>Agent Coaching Snapshot</CardTitle>
          </CardHeader>
          <CardContent>
            {agentLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} loading className="h-12 w-full" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Top Performers */}
                <div>
                  <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-green-600">
                    <Icon name="trend-up" className="h-4 w-4" />
                    Top Performers
                  </h4>
                  {topAgents.length === 0 ? (
                    <EmptyState message="No agents with sufficient volume" />
                  ) : (
                    <AgentTable agents={topAgents} />
                  )}
                </div>

                {/* Needs Coaching */}
                <div>
                  <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-red-500">
                    <Icon name="trend-down" className="h-4 w-4" />
                    Needs Coaching
                  </h4>
                  {bottomAgents.length === 0 ? (
                    <EmptyState message="No agents with sufficient volume" />
                  ) : (
                    <AgentTable agents={bottomAgents} />
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Intent + Sentiment */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Intent Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Quality by Intent</CardTitle>
          </CardHeader>
          <CardContent>
            {intentLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} loading className="h-12 w-full" />
                ))}
              </div>
            ) : intents.length === 0 ? (
              <EmptyState message="No intent data with sufficient volume" />
            ) : (
              <IntentTable intents={intents} />
            )}
          </CardContent>
        </Card>

        {/* Sentiment Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Quality by Sentiment</CardTitle>
          </CardHeader>
          <CardContent>
            {sentimentLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} loading className="h-12 w-full" />
                ))}
              </div>
            ) : sentiments.length === 0 ? (
              <EmptyState message="No sentiment data with sufficient volume" />
            ) : (
              <SentimentTable sentiments={sentiments} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// ── Sub-Components ────────────────────────────────────────────────────────

function KpiCard(props: {
  title: string
  value: number | null
  previousValue: number | null
  format: (v: number) => string
  isLoading?: boolean
  trend?: number | null
}) {
  const computedTrend =
    props.trend !== undefined
      ? props.trend
      : props.value !== null && props.previousValue !== null && props.previousValue !== 0
        ? (props.value - props.previousValue) / props.previousValue
        : null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-muted-foreground text-sm font-medium">
          {props.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {props.isLoading ? (
          <Skeleton loading className="h-8 w-24" />
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold">
              {props.value !== null ? props.format(props.value) : '—'}
            </span>
            {computedTrend !== null && (
              <Badge variant={computedTrend >= 0 ? 'success' : 'destructive'} size="sm">
                <Icon
                  name={computedTrend >= 0 ? 'arrow-up' : 'arrow-down'}
                  className="mr-0.5 h-3 w-3"
                />
                {toPercent(Math.abs(computedTrend))}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function AgentTable(props: {
  agents: Array<{
    id?: string
    firstName?: string
    lastName?: string
    avatar?: string
    avgScore: number
    totalEvals: number
    coverage: number
  }>
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Agent</TableHead>
          <TableHead className="text-right">Score</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {props.agents.map((agent) => (
          <TableRow key={agent.id ?? Math.random()}>
            <TableCell>
              {agent.id ? (
                <Link
                  to="/$alias/members/$memberId"
                  params={{ memberId: agent.id }}
                  from="/$alias"
                  className="flex items-center gap-2"
                >
                  <Avatar
                    firstName={agent.firstName}
                    lastName={agent.lastName}
                    avatarFileId={agent.avatar}
                    size="sm"
                  />
                  <span className="text-sm">
                    {agent.firstName} {agent.lastName}
                  </span>
                </Link>
              ) : (
                <span className="text-sm">Unknown</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <span
                className={cn('font-mono', {
                  'text-red-500': agent.avgScore < 0.7,
                  'text-yellow-500': agent.avgScore >= 0.7 && agent.avgScore < 0.9,
                  'text-green-500': agent.avgScore >= 0.9,
                })}
              >
                {toPercent(agent.avgScore)}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function IntentTable(props: {
  intents: Array<{
    id?: string
    name: string
    avgScore: number
    count: number
  }>
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Intent</TableHead>
          <TableHead className="text-right">Calls</TableHead>
          <TableHead className="text-right">Avg Score</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {props.intents.map((intent) => (
          <TableRow key={intent.id ?? intent.name}>
            <TableCell className="font-medium">{intent.name}</TableCell>
            <TableCell className="text-right font-mono">
              {intent.count.toLocaleString()}
            </TableCell>
            <TableCell className="text-right">
              <span
                className={cn('font-mono', {
                  'text-red-500': intent.avgScore < 0.7,
                  'text-yellow-500': intent.avgScore >= 0.7 && intent.avgScore < 0.9,
                  'text-green-500': intent.avgScore >= 0.9,
                })}
              >
                {toPercent(intent.avgScore)}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function SentimentTable(props: {
  sentiments: Array<{
    id?: string
    name: string
    color?: string
    avgScore: number
    count: number
  }>
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Sentiment</TableHead>
          <TableHead className="text-right">Calls</TableHead>
          <TableHead className="text-right">Avg Score</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {props.sentiments.map((s, index) => (
          <TableRow key={s.id ?? s.name}>
            <TableCell>
              <div className="flex items-center gap-2">
                {s.color && (
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                )}
                <span className="font-medium">
                  {index === 0 ? 'Negative' : index === 1 ? 'Positive' : 'Neutral'}
                </span>
              </div>
            </TableCell>
            <TableCell className="text-right font-mono">
              {s.count.toLocaleString()}
            </TableCell>
            <TableCell className="text-right">
              <span
                className={cn('font-mono', {
                  'text-red-500': s.avgScore < 0.7,
                  'text-yellow-500': s.avgScore >= 0.7 && s.avgScore < 0.9,
                  'text-green-500': s.avgScore >= 0.9,
                })}
              >
                {toPercent(s.avgScore)}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function EmptyState(props: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Icon
        name="chart-bar"
        className="text-muted-foreground/50 mb-2 h-8 w-8"
        weight="fill"
      />
      <p className="text-muted-foreground text-sm">{props.message}</p>
    </div>
  )
}
